import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Voyage } from '../core/_models';
import { getActiveVoyageForVessel, startVoyage, endVoyage } from '../core/_requests';
import { getApiErrorMessage } from '../../../shared/utils/apiErrorMessage';
import { KTSVG } from '../../../../_metronic/helpers';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    voyage: Voyage;
    onVoyageStatusUpdated: (updatedVoyage: Voyage) => void;
}

const isVoyageRestartDisabled = (voyage: Voyage) => {
    if (!voyage) return false;
    const status = String(voyage?.status || '').trim().toLowerCase();
    return status === 'completed' || status === 'ended' || status === 'inactive';
};

const StartEndVoyageModal: React.FC<Props> = ({ isOpen, onClose, voyage, onVoyageStatusUpdated }) => {
    const [canStart, setCanStart] = useState(false);
    const [activeVoyageNumber, setActiveVoyageNumber] = useState('');
    const [activeVoyage, setActiveVoyage] = useState(false);
    const [loading, setLoading] = useState(false);
    const restartDisabled = isVoyageRestartDisabled(voyage);

    useEffect(() => {
        if (!isOpen) return;

        const fetchStatus = async () => {
            if (restartDisabled) {
                setCanStart(false);
                return;
            }

            if (voyage.active === true) {
                setCanStart(false);
            } else {
                setCanStart(true);
            }
        };

        fetchStatus();
    }, [isOpen, voyage, restartDisabled]);

    useEffect(() => {
        if (!isOpen) return;

        const fetchStatus = async () => {
            setLoading(true);
            setActiveVoyage(false);
            try {
                if (restartDisabled) {
                    setCanStart(false);
                    setActiveVoyageNumber(voyage.voyageNumber || '');
                    return;
                }

                const response = await getActiveVoyageForVessel(voyage.vessel.id);
                const currentActive = Array.isArray(response) ? response[0] : null;
                const currentNumber = currentActive?.voyageNumber || '';

                setActiveVoyageNumber(currentNumber);
                if (currentNumber && currentNumber === voyage.voyageNumber) {
                    setActiveVoyage(true);
                }
                setCanStart(false);
            } catch (error: any) {
                const errMsg =
                    error?.response?.data?.message ??
                    error?.message ??
                    'Unknown error occurred';

                if (errMsg.includes("No active voyages with status 'started'")) {
                    setCanStart(!restartDisabled);
                } else {
                    setCanStart(false);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchStatus();
    }, [isOpen, voyage, restartDisabled]);

    const handleStart = async () => {
        if (restartDisabled) return;

        try {
            const res = await startVoyage(voyage.id);
            const updated = {
                ...voyage,
                ...(res && res.id ? res : {}),
                active: true,
                status: res?.status || 'started',
            };
            toast.success(`Voyage ${voyage.voyageNumber} started successfully.`);
            onVoyageStatusUpdated(updated);
            onClose();
        } catch (error: any) {
            console.error('Failed to start voyage:', error);
            toast.error(getApiErrorMessage(error, 'Failed to start voyage.'));
        }
    };

    const handleEnd = async () => {
        try {
            const res = await endVoyage(voyage.id);
            const updated = {
                ...voyage,
                ...(res && res.id ? res : {}),
                active: false,
                status: res?.status || 'completed',
            };
            toast.success(`Voyage ${voyage.voyageNumber} ended successfully.`);
            onVoyageStatusUpdated(updated);
            onClose();
        } catch (error: any) {
            console.error('Failed to end voyage:', error);
            toast.error(getApiErrorMessage(error, 'Failed to end voyage.'));
        }
    };

    if (!isOpen) return null;

    return (
        <div className='modal-overlay'>
            <div className='modal-content' onClick={(e) => e.stopPropagation()}>
                <div className='custom-modal-header d-flex justify-content-between align-items-center'>
                    <h5 className='m-0'>Manage Voyage</h5>
                    <button className='close-btn' onClick={onClose}>
                        <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
                    </button>
                </div>

                <div className='custom-modal-body'>
                    {loading ? (
                        <p>Loading status...</p>
                    ) : restartDisabled ? (
                        <div className='alert alert-warning'>
                            Voyage {voyage.voyageNumber} is completed and cannot be started again.
                        </div>
                    ) : canStart ? (
                        <p>You can start this voyage.</p>
                    ) : (
                        <div className='alert alert-warning'>
                            Voyage {activeVoyageNumber} is active for this vessel.
                            <br />
                            Please end it before starting another one.
                        </div>
                    )}

                    <div>
                        {canStart && !restartDisabled ? (
                            <button className='btn btn_success' onClick={handleStart}>
                                Start Voyage
                            </button>
                        ) : activeVoyage ? (
                            <button className='btn btn_danger' onClick={handleEnd}>
                                End Voyage
                            </button>
                        ) : null}
                    </div>
                </div>

                <div className='d-flex justify-content-end gap-2'>
                    <button className='btn btn_secondary' onClick={onClose}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StartEndVoyageModal;
